import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type FC,
  type ReactElement,
  type ReactNode,
} from 'react';
import { styled } from '@mui/material/styles';

export type ListSlideDirection = 'left' | 'right' | 'top' | 'bottom';

export interface WithChildrenSlideAnimationProps {
  animationDirection?: ListSlideDirection;
  animationStepDelay?: number;
  children?: ReactNode;
  onEach?: ({
    htmlElement,
    index,
  }: {
    htmlElement: HTMLElement | null;
    index: number;
  }) => void;
  onEnd?: () => void;
  onStart?: () => void;
}

const animationTransforms = {
  right: 'translateX(-40px)',
  left: 'translateX(40px)',
  bottom: 'translateY(-40px)',
  top: 'translateY(40px)',
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function withChildrenSlideAnimation<P = {}>(
  Component: ComponentType<P>
) {
  const Styled = styled(Component, {
    shouldForwardProp: (prop: string) =>
      !['direction', 'animationStepDelay', 'animationDirection'].includes(prop),
  })<{ direction?: ListSlideDirection }>(({ direction = 'right' }) => ({
    '& > *': {
      opacity: 0,
      animation: `slideTo-${direction} 0.6s forwards`,
    },
    [`@keyframes slideTo-${direction}`]: {
      from: { opacity: 0, transform: animationTransforms[direction] },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
  }));

  const AnimatedComponent: FC<P & WithChildrenSlideAnimationProps> = ({
    children,
    animationDirection = 'right',
    animationStepDelay = 180,
    onEach,
    onEnd,
    onStart,
    ...rest
  }) => {
    const [visibleCount, setVisibleCount] = useState(0);
    const prevChildrenLength = useRef(0);
    const hasAnimated = useRef(false);
    // Track which indices have already fired onEach
    const calledIndices = useRef<Set<number>>(new Set());
    const childrenArray = Children.toArray(children);
    const endTimeout = useRef<NodeJS.Timeout | null>(null);

    // Fire onEnd only once per animation sequence
    const hasFiredEnd = useRef(false);
    // Fire onStart only once per animation sequence
    const hasFiredStart = useRef(false);

    useEffect(() => {
      if (
        !!onEnd &&
        visibleCount === childrenArray.length &&
        childrenArray.length > 0 &&
        !hasFiredEnd.current
      ) {
        hasFiredEnd.current = true;
        if (endTimeout.current) clearTimeout(endTimeout.current);
        endTimeout.current = setTimeout(() => {
          onEnd();
        }, animationStepDelay);
      }
      return () => {
        if (endTimeout.current) clearTimeout(endTimeout.current);
      };
    }, [visibleCount, childrenArray.length, animationStepDelay, onEnd]);

    useEffect(() => {
      hasFiredEnd.current = false;
      hasFiredStart.current = false;
    }, [childrenArray.length]);

    useEffect(() => {
      // On mount or when children are added, animate in new children
      const start = hasAnimated.current ? prevChildrenLength.current : 0;
      if (childrenArray.length > start) {
        // Call onStart once per animation sequence
        if (onStart && !hasFiredStart.current) {
          onStart();
          hasFiredStart.current = true;
        }
        let i = start;
        const showNext = () => {
          setVisibleCount((prev) => prev + 1);
          i++;
          if (i < childrenArray.length) {
            setTimeout(showNext, animationStepDelay);
          }
        };
        showNext();
        prevChildrenLength.current = childrenArray.length;
      }
      if (!hasAnimated.current) {
        hasAnimated.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childrenArray.length, animationStepDelay]);

    return (
      <Styled direction={animationDirection} {...(rest as P)}>
        {childrenArray.slice(0, visibleCount).map((child, i) => {
          const ref = (htmlElement: HTMLElement | null) => {
            if (htmlElement && onEach && !calledIndices.current.has(i)) {
              calledIndices.current.add(i);
              onEach({ index: i, htmlElement });
            }
          };
          if (isValidElement(child)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return cloneElement(child as ReactElement<any>, {
              ref,
            });
          }
          return child;
        })}
      </Styled>
    );
  };

  return AnimatedComponent;
}
